import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import {
    MdArrowBack, MdLocationOn, MdWork, MdPerson, MdWarning, MdFolder, MdArrowForward,
    MdHistory, MdSchedule, MdClose, MdAttachMoney, MdSearch, MdNotificationsActive,
    MdAccessTime, MdFilterList, MdDashboard, MdCheckCircle, MdLogout, MdChevronLeft,
    MdEmail, MdPhone, MdChat, MdInfo, MdDirectionsCar, MdCameraAlt, MdDraw, MdTimer,
    MdRefresh, MdVisibility, MdAssignment, MdVerifiedUser, MdSettings, MdPhotoCamera
} from 'react-icons/md';
import { format, formatDistanceToNow } from 'date-fns';
import logo from '../assets/logo.png';

// PREMIUM LIGHT THEME - Roboto Font Family
// Primary: #008891 (Teal)
// Secondary: #0f3057 (Navy)
// Background: #F8FAFC
// Using light colors, minimal heights, thin borders

const EngineerDashboard = () => {
    const [user, setUser] = useState(null);
    const [view, setView] = useState('dashboard');
    const [manualReportCenterId, setManualReportCenterId] = useState('');
    const [centers, setCenters] = useState([]);
    const [myVisits, setMyVisits] = useState([]);
    const [currentVisit, setCurrentVisit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [location, setLocation] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedCenter, setSelectedCenter] = useState(null);
    const [centerReports, setCenterReports] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);

    useEffect(() => {
        if (selectedCenter) {
            fetchCenterHistory(selectedCenter.id);
        }
    }, [selectedCenter]);

    const fetchCenterHistory = async (centerId) => {
        setLoadingHistory(true);
        try {
            const res = await api.get('/hardware/reports');

            // Try multiple possible field names for center ID
            const history = res.data.filter(r => {
                return String(r.center_id) === String(centerId) ||
                    String(r.user_id) === String(centerId) ||
                    String(r.id) === String(centerId);
            }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setCenterReports(history);
        } catch (error) {
            console.error("Error fetching history", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Forms State
    const [scheduleData, setScheduleData] = useState({ center_id: null, report_id: null, date: '' });
    const [workData, setWorkData] = useState({
        work_description: '',
        parts_used: 'No',
        issue_resolved: 'Yes',
        parts_left_onsite: 'No',
        visit_required: 'No',
        installed_parts: '',
        removed_parts: '',
        customer_name: '',
        parts_needed: '',
        arrival_time: '',
        offsite_time: '',
        ticket_number: '',
        removed_part_numbers: '',
        installed_part_numbers: '',
        removed_part_serials: '',
        installed_part_serials: '',
        poc_arrival_correct: 'Yes',
        poc_satisfaction: 'Yes',
        poc_comments: '',
        poc_comments: '',
        repair_photo: null,
        manager_photo: null
    });
    const [engineerSign, setEngineerSign] = useState(null);
    const [checkoutData, setCheckoutData] = useState({ travel_cost: '', food_cost: '', evidence_photo: null });
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
    const [visitData, setVisitData] = useState({
        visit_date: '',
        visit_time: '',
        remarks: ''
    });

    // Workflow States
    // Workflow States (6-Step System Verified)
    const [arrivalPhoto, setArrivalPhoto] = useState(null);
    const [arrivalSignatureData, setArrivalSignatureData] = useState(null); // Step 3
    const [signatureData, setSignatureData] = useState(null); // Step 5 Work Sign
    const [contactSign, setContactSign] = useState(null);
    const [showArrivalModal, setShowArrivalModal] = useState(false);
    const [showWorkModal, setShowWorkModal] = useState(false); // Step 5
    const [showFinalModal, setShowFinalModal] = useState(false); // Step 6
    const [activeWorkflowVisit, setActiveWorkflowVisit] = useState(null);
    const [partsNeeded, setPartsNeeded] = useState('');

    // Ad-Hoc Audit State
    const [auditCenterId, setAuditCenterId] = useState('');
    const [auditStep, setAuditStep] = useState(0); // 0: Checklist, 1: Photo, 2: Signature
    const [auditSelfie, setAuditSelfie] = useState(null);
    const [auditSign, setAuditSign] = useState(null);
    const [auditItems, setAuditItems] = useState([
        { id: Date.now(), sn: 1, category: 'Computer', problem: '', req_assets: '' }
    ]);

    // Step 3.5: Inspection Report State
    const [showInspectionModal, setShowInspectionModal] = useState(false);
    const [inspectionItems, setInspectionItems] = useState([
        { id: Date.now(), sn: 1, category: 'Computer', problem: '', req_assets: '' }
    ]);

    const handleDepart = async (visitId) => {
        if (!window.confirm("Start journey to center?")) return;
        try {
            await api.post(`/hardware/visit/${visitId}/depart`);
            fetchInitialData();
        } catch (e) { alert("Error starting journey"); }
    };

    // Detailed Report View State
    const [viewReport, setViewReport] = useState(null);

    const handleWorkflowArrive = (visit) => {
        setActiveWorkflowVisit(visit);
        setShowArrivalModal(true);
    };

    // Multiple Hardware Items Solution
    const [hardwareItems, setHardwareItems] = useState([
        { id: Date.now(), removed_no: '', removed_sn: '', installed_no: '', installed_sn: '' }
    ]);

    const addHardwareItem = () => {
        setHardwareItems([...hardwareItems, { id: Date.now(), removed_no: '', removed_sn: '', installed_no: '', installed_sn: '' }]);
    };

    const removeHardwareItem = (id) => {
        if (hardwareItems.length > 1) {
            setHardwareItems(hardwareItems.filter(item => item.id !== id));
        }
    };

    const updateHardwareItem = (id, field, value) => {
        setHardwareItems(hardwareItems.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const confirmArrive = async () => {
        if (!arrivalPhoto) return alert("Selfie required!");
        if (!arrivalSignatureData) return alert("Center Head Signature required!");
        setSubmitting(true);
        const formData = new FormData();
        formData.append('center_id', activeWorkflowVisit.center_id);
        formData.append('visit_id', activeWorkflowVisit.id);
        formData.append('arrival_photo', arrivalPhoto);
        formData.append('signature', arrivalSignatureData);
        if (location) { formData.append('latitude', location.latitude); formData.append('longitude', location.longitude); }

        try {
            await api.post('/hardware/check-in', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setShowArrivalModal(false);
            setArrivalPhoto(null);
            setArrivalSignatureData(null);
            fetchInitialData();
            alert("Check-in successful! Status: Working.");
        } catch (e) { alert("Check-in failed"); }
        finally { setSubmitting(false); }
    };

    const handleWorkflowInspection = (visit) => {
        setActiveWorkflowVisit(visit);
        setShowInspectionModal(true);
    };

    const addInspectionItem = () => {
        setInspectionItems([...inspectionItems, { id: Date.now(), sn: inspectionItems.length + 1, category: '', problem: '', req_assets: '' }]);
    };

    const removeInspectionItem = (id) => {
        if (inspectionItems.length === 1) return;
        setInspectionItems(inspectionItems.filter(item => item.id !== id));
    };

    const updateInspectionItem = (id, field, value) => {
        setInspectionItems(inspectionItems.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const confirmSubmitInspection = async () => {
        const isValid = inspectionItems.every(item => item.category && item.problem !== '');
        if (!isValid) return alert("Please fill at least the item name, total and problem counts.");

        setSubmitting(true);
        try {
            await api.post(`/hardware/visit/${activeWorkflowVisit.id}/inspection`, {
                inspection_data: JSON.stringify(inspectionItems)
            });
            setShowInspectionModal(false);
            setInspectionItems([{ id: Date.now(), sn: 1, category: 'Computer', problem: '', req_assets: '' }]);
            fetchInitialData();
            alert("Digital Inspection Report submitted successfully!");
        } catch (e) {
            console.error(e);
            alert("Failed to submit inspection report.");
        } finally {
            setSubmitting(false);
        }
    };

    const confirmSubmitAdHocAudit = async () => {
        if (auditStep === 0) {
            if (!auditCenterId) return alert("Please select a center.");
            const isValid = auditItems.every(item => item.category && item.problem !== '');
            if (!isValid) return alert("Please fill at least the item name, total and problem counts.");
            setAuditStep(1); // Show Photo Modal
            return;
        }

        if (auditStep === 1) {
            if (!auditSelfie) return alert("Live Photo with Center Head is required!");
            setAuditStep(2); // Show Signature Modal
            return;
        }

        if (auditStep === 2) {
            if (!auditSign) return alert("Center Head Signature is required!");

            setSubmitting(true);
            const formData = new FormData();
            formData.append('center_id', auditCenterId);
            formData.append('inspection_data', JSON.stringify(auditItems));
            formData.append('selfie_photo', auditSelfie);
            formData.append('signature', auditSign);
            if (location) {
                formData.append('latitude', location.latitude);
                formData.append('longitude', location.longitude);
            }

            try {
                await api.post('/hardware/visit/quick-audit', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert("Digital Audit Visit Report submitted successfully!");
                // Reset everything
                setAuditCenterId('');
                setAuditSelfie(null);
                setAuditSign(null);
                setAuditStep(0);
                setAuditItems([{ id: Date.now(), sn: 1, category: 'Computer', problem: '', req_assets: '' }]);
                setView('dashboard');
                fetchInitialData();
            } catch (e) {
                console.error(e);
                alert("Failed to submit audit report.");
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleWorkflowWork = (visit) => {
        setActiveWorkflowVisit(visit);
        setShowWorkModal(true);
    };

    const confirmSubmitManualWork = async () => {
        if (!manualReportCenterId) return alert("Please select a center!");
        if (!workData.work_description) return alert("Please provide a work description!");
        if (!signatureData) return alert("Center Head Signature is mandatory!");
        if (!engineerSign) return alert("Engineer Signature is mandatory!");

        setSubmitting(true);
        const formData = new FormData();
        formData.append('center_id', manualReportCenterId);
        formData.append('work_description', workData.work_description);
        formData.append('parts_used', workData.parts_used);
        formData.append('issue_resolved', workData.issue_resolved);
        formData.append('parts_left_onsite', workData.parts_left_onsite);
        formData.append('visit_required', workData.visit_required);
        formData.append('customer_name', workData.customer_name);
        formData.append('parts_needed', partsNeeded);

        // Consolidate hardware items for backend
        const remNos = hardwareItems.map(h => h.removed_no).filter(x => x).join(' | ');
        const remSNs = hardwareItems.map(h => h.removed_sn).filter(x => x).join(' | ');
        const insNos = hardwareItems.map(h => h.installed_no).filter(x => x).join(' | ');
        const insSNs = hardwareItems.map(h => h.installed_sn).filter(x => x).join(' | ');

        formData.append('removed_part_numbers', remNos);
        formData.append('removed_part_serials', remSNs);
        formData.append('installed_part_numbers', insNos);
        formData.append('installed_part_serials', insSNs);

        formData.append('signature', signatureData); // POC Signature
        formData.append('engineer_signature', engineerSign); // Engineer Signature

        // Exact Form Fields
        formData.append('arrival_time', workData.arrival_time);
        formData.append('offsite_time', workData.offsite_time);
        formData.append('poc_arrival_correct', workData.poc_arrival_correct);
        formData.append('poc_satisfaction', workData.poc_satisfaction);
        formData.append('poc_comments', workData.poc_comments);

        if (checkoutData.evidence_photo) formData.append('evidence_photo', checkoutData.evidence_photo);

        try {
            await api.post(`/hardware/manual-work-reports`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSignatureData(null);
            setEngineerSign(null);
            setWorkData({
                work_description: '',
                parts_used: 'No',
                issue_resolved: 'Yes',
                parts_left_onsite: 'No',
                visit_required: 'No',
                installed_parts: '',
                removed_parts: '',
                customer_name: '',
                ticket_number: '',
                arrival_time: '',
                offsite_time: '',
                removed_part_numbers: '',
                installed_part_numbers: '',
                removed_part_serials: '',
                installed_part_serials: '',
                poc_arrival_correct: 'Yes',
                poc_satisfaction: 'Yes',
                poc_comments: '',
                repair_photo: null,
                manager_photo: null
            });
            setPartsNeeded('');
            setManualReportCenterId('');
            setHardwareItems([{ id: Date.now(), removed_no: '', removed_sn: '', installed_no: '', installed_sn: '' }]);
            setCheckoutData({ ...checkoutData, evidence_photo: null });
            fetchInitialData();
            alert("Manual Work Report Submitted Successfully!");
            setView('dashboard');
        } catch (e) {
            console.error(e);
            alert("Manual report submission failed. Error: " + (e.response?.data?.message || e.message));
        }
        finally { setSubmitting(false); }
    };

    const confirmSubmitWork = async () => {
        if (!signatureData) return alert("Center Head Signature required!");
        setSubmitting(true);
        const formData = new FormData();
        formData.append('work_description', workData.work_description);
        formData.append('parts_used', workData.parts_used);
        formData.append('issue_resolved', workData.issue_resolved);
        formData.append('parts_left_onsite', workData.parts_left_onsite);
        formData.append('visit_required', workData.visit_required);
        formData.append('installed_parts', workData.installed_parts);
        formData.append('removed_parts', workData.removed_parts);
        formData.append('customer_name', workData.customer_name);
        formData.append('parts_needed', partsNeeded);
        formData.append('signature', signatureData); // POC
        formData.append('engineer_signature', engineerSign); // Engineer

        // Exact Form Fields
        formData.append('ticket_number', workData.ticket_number);
        formData.append('arrival_time', workData.arrival_time);
        formData.append('offsite_time', workData.offsite_time);
        formData.append('removed_part_numbers', workData.removed_part_numbers);
        formData.append('installed_part_numbers', workData.installed_part_numbers);
        formData.append('removed_part_serials', workData.removed_part_serials);
        formData.append('installed_part_serials', workData.installed_part_serials);
        formData.append('poc_arrival_correct', workData.poc_arrival_correct);
        formData.append('poc_satisfaction', workData.poc_satisfaction);
        formData.append('poc_comments', workData.poc_comments);

        if (checkoutData.evidence_photo) formData.append('evidence_photo', checkoutData.evidence_photo);

        try {
            await api.post(`/hardware/visit/${activeWorkflowVisit.id}/report`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setShowWorkModal(false);
            setSignatureData(null);
            setWorkData({ ...workData, work_description: '' });
            setPartsNeeded('');
            setCheckoutData({ ...checkoutData, evidence_photo: null });
            fetchInitialData();
            alert("Work Report Submitted! Status: Work Done.");
        } catch (e) { alert("Report submission failed"); }
        finally { setSubmitting(false); }
    };

    const handleWorkflowFinal = (visit) => {
        setActiveWorkflowVisit(visit);
        setShowFinalModal(true);
    };

    const confirmFinalClose = async () => {
        setSubmitting(true);
        const formData = new FormData();
        formData.append('travel_cost', checkoutData.travel_cost || 0);
        formData.append('food_cost', checkoutData.food_cost || 0);
        if (location) { formData.append('latitude', location.latitude); formData.append('longitude', location.longitude); }

        try {
            await api.post(`/hardware/visit/${activeWorkflowVisit.id}/complete`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setShowFinalModal(false);
            setCheckoutData({ travel_cost: '', food_cost: '', evidence_photo: null });
            fetchInitialData();
            setView('dashboard');
            alert("Mission Closed Successfully!");
        } catch (e) { alert("Final close failed"); }
        finally { setSubmitting(false); }
    };

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to sign out?")) {
            localStorage.removeItem('token');
            localStorage.removeItem('user_role');
            window.location.href = '/login';
        }
    };

    const requestLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => setLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
                (error) => console.error("Location Error:", error)
            );
        }
    };

    const fetchUser = async () => {
        try {
            const res = await api.get('/user');
            setUser(res.data);
        } catch (error) { console.error("Error fetching user", error); }
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            // OPTIMIZATION: Parallel API calls instead of sequential
            const [centersRes, visitsRes, currentVisitRes, userRes] = await Promise.all([
                api.get('/hardware/centers-enhanced'),
                api.get('/hardware/visits'),
                api.get('/hardware/current-visit'),
                api.get('/user')
            ]);

            // Process centers
            const sorted = centersRes.data.sort((a, b) => {
                if (a.is_urgent && !b.is_urgent) return -1;
                if (!a.is_urgent && b.is_urgent) return 1;
                return new Date(b.latest_report_date || 0) - new Date(a.latest_report_date || 0);
            });
            setCenters(sorted);

            // Set visits
            setMyVisits(visitsRes.data);

            // Set current visit if exists
            if (currentVisitRes.data.visit) {
                setCurrentVisit(currentVisitRes.data.visit);
            }

            // Set user
            setUser(userRes.data);

        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchReportFeed = async () => {
        try {
            const res = await api.get('/hardware/centers-enhanced');
            const sorted = res.data.sort((a, b) => {
                if (a.is_urgent && !b.is_urgent) return -1;
                if (!a.is_urgent && b.is_urgent) return 1;
                return new Date(b.latest_report_date || 0) - new Date(a.latest_report_date || 0);
            });
            setCenters(sorted);
        } catch (error) {
            console.error("Error fetching centers", error);
        }
    };

    const fetchVisits = async () => {
        try {
            const res = await api.get('/hardware/visits');
            setMyVisits(res.data);
        } catch (error) { console.error("Error fetching visits", error); }
    };

    useEffect(() => {
        fetchInitialData();
        // OPTIMIZATION: Request location in background, non-blocking
        requestLocation();
    }, []);

    const handleSchedule = async () => {
        setSubmitting(true);
        try {
            await api.post('/hardware/schedule-visit', {
                center_id: scheduleData.center_id,
                report_id: scheduleData.report_id,
                scheduled_date: scheduleData.date
            });
            alert("Visit Scheduled Successfully!");
            setShowScheduleModal(false);
            setSelectedCenter(null);
            fetchReportFeed();
            fetchVisits();
        } catch (error) { alert(error.response?.data?.message || 'Failed'); }
        finally { setSubmitting(false); }
    };

    const handleCheckIn = async (centerId) => {
        if (!location) { requestLocation(); alert("Waiting for GPS..."); return; }
        setSubmitting(true);
        try {
            const res = await api.post('/hardware/check-in', { center_id: centerId, latitude: location.latitude, longitude: location.longitude });
            setCurrentVisit(res.data.visit);
            setView('active_work');
            setSelectedCenter(null);
        } catch (error) { alert(error.response?.data?.message || 'Check-in failed'); }
        finally { setSubmitting(false); }
    };

    const handleSubmitWork = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const formData = new FormData();
        formData.append('work_description', workData.work_description);
        if (workData.repair_photo) formData.append('repair_photo', workData.repair_photo);
        if (workData.manager_photo) formData.append('manager_photo', workData.manager_photo);

        try {
            const res = await api.post(`/hardware/submit-work/${currentVisit.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setCurrentVisit(res.data.visit);
            setView('active_checkout');
        } catch (error) { alert(error.response?.data?.message || 'Submission failed'); }
        finally { setSubmitting(false); }
    };

    const handleCheckOut = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const formData = new FormData();
        formData.append('work_description', currentVisit.work_description || "Completed");
        formData.append('travel_cost', checkoutData.travel_cost);
        formData.append('food_cost', checkoutData.food_cost);
        if (checkoutData.evidence_photo) formData.append('evidence_photo', checkoutData.evidence_photo);
        if (location) { formData.append('latitude', location.latitude); formData.append('longitude', location.longitude); }

        try {
            await api.post(`/hardware/check-out/${currentVisit.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setCurrentVisit(null);
            setView('reports');
            fetchInitialData();
            alert("Visit Completed!");
        } catch (error) { alert(error.response?.data?.message || 'Checkout failed'); }
        finally { setSubmitting(false); }
    };

    // Handle Schedule Visit
    const handleScheduleVisit = async () => {
        if (!visitData.visit_date || !visitData.remarks) {
            alert('Please fill visit date and remarks');
            return;
        }

        try {
            const payload = {
                center_id: selectedCenter.id,
                center_name: selectedCenter.center_name,
                visit_date: visitData.visit_date,
                visit_time: visitData.visit_time,
                remarks: visitData.remarks,
                engineer_id: user.id,
                engineer_name: user.name
            };

            await api.post('/hardware/schedule-visit', payload);
            alert('Visit scheduled successfully! Admin and center will be notified.');
            setShowScheduleVisitModal(false);
            setVisitData({ visit_date: '', visit_time: '', remarks: '' });
            fetchInitialData();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to schedule visit');
        }
    };

    const FileInput = ({ label, icon: Icon, onChange, value }) => (
        <div className="border border-dashed border-slate-200 bg-white/50 rounded-lg p-3 text-center hover:bg-teal-50/30 hover:border-[#008891]/30 relative transition-all group cursor-pointer h-24 flex flex-col items-center justify-center">
            <input type="file" accept="image/*" onChange={onChange} className="absolute inset-0 opacity-0 cursor-pointer" required />
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-1.5 transition-colors ${value ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-400 group-hover:bg-[#008891]/10 group-hover:text-[#008891]'}`}>
                {value ? <MdCheckCircle size={20} /> : <Icon size={20} />}
            </div>
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide group-hover:text-[#008891] transition-colors">{value ? "âœ“ Attached" : label}</span>
        </div>
    );

    // OPTIMIZATION: useMemo hooks MUST be called before any conditional returns (React Hooks Rule)
    const filteredCenters = React.useMemo(() =>
        centers.filter(c => c.center_name.toLowerCase().includes(searchQuery.toLowerCase())),
        [centers, searchQuery]
    );

    const urgentReports = React.useMemo(() =>
        filteredCenters.filter(c => c.is_urgent && c.latest_report_id && !c.is_resolved),
        [filteredCenters]
    );

    const resolvedReports = React.useMemo(() =>
        filteredCenters.filter(c => c.is_resolved),
        [filteredCenters]
    );

    const recentReports = React.useMemo(() =>
        filteredCenters.filter(c => !c.is_urgent && c.latest_report_id && !c.is_resolved),
        [filteredCenters]
    );

    const allOtherCenters = React.useMemo(() =>
        filteredCenters.filter(c => !c.latest_report_id),
        [filteredCenters]
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="w-10 h-10 border-4 border-[#008891] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 font-['Roboto',sans-serif] pb-20">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
            `}</style>

            {/* --- PREMIUM LIGHT HEADER (Only show when not in detail view) --- */}
            {!selectedCenter && (<div>
                <div className="bg-white/90 backdrop-blur-md py-3 px-4 border-b border-slate-200/50 sticky top-0 z-40 shadow-sm">
                    <div className="flex justify-between items-center max-w-7xl mx-auto">
                        {/* Brand Logo Area */}
                        {!showMobileSearch && (
                            <div className="flex items-center gap-2 cursor-pointer animate-in fade-in" onClick={() => setView('reports')}>
                                <img src={logo} alt="Secure Insights" className="w-8 h-5 sm:w-10 sm:h-7" />
                                <div>
                                    <h1 className="text-sm sm:text-base font-semibold text-slate-800 leading-none tracking-tight">
                                        Secure<span className="text-[#008891]">Insights</span>
                                    </h1>
                                </div>
                            </div>
                        )}

                        {/* Search Input (Overlay Style on Mobile, Sidebar/Header style elsewhere) */}
                        <div className={`flex-1 max-w-lg mx-4 transition-all duration-300 ${showMobileSearch ? 'flex' : 'hidden md:flex'}`}>
                            <div className="relative w-full">
                                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder="Search centers or reports..."
                                    className="w-full bg-slate-100/50 text-slate-800 placeholder-slate-400 border border-slate-200/60 rounded-full py-2 pl-9 pr-10 focus:ring-2 focus:ring-[#008891]/20 focus:bg-white outline-none transition-all text-xs font-normal"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {showMobileSearch && (
                                    <button
                                        onClick={() => { setShowMobileSearch(false); setSearchQuery(''); }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 p-1 md:hidden"
                                    >
                                        <MdClose size={14} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Action Icons */}
                        <div className="flex items-center gap-3">
                            {/* Mobile Search Toggle */}
                            {!showMobileSearch && (
                                <button
                                    onClick={() => setShowMobileSearch(true)}
                                    className="md:hidden w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <MdSearch size={22} />
                                </button>
                            )}

                            {/* User Profile Hook with Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    className="h-8 w-8 rounded-full bg-gradient-to-br from-teal-50 to-blue-50 border border-slate-200/60 flex items-center justify-center text-[#0f3057] font-semibold text-xs hover:border-[#008891]/40 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#008891]/20 overflow-hidden"
                                >
                                    {user?.profile_image ? (
                                        <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        user?.name?.charAt(0) || 'E'
                                    )}
                                </button>

                                {/* Dropdown Menu */}
                                {showProfileMenu && (
                                    <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-lg shadow-lg border border-slate-100/80 py-0.5 z-40 animate-fade-in-up origin-top-right">
                                        <button
                                            onClick={() => { setShowProfileModal(true); setShowProfileMenu(false); }}
                                            className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 font-medium transition-colors"
                                        >
                                            <MdPerson size={16} className="text-[#008891]" /> My Profile
                                        </button>
                                        <div className="h-px bg-slate-100 my-0.5 mx-2" />
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium transition-colors"
                                        >
                                            <MdLogout size={16} /> Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- MAIN CONTENT --- */}
                <div className="px-4 mt-4 relative z-20 max-w-7xl mx-auto">

                    {/* ACTIVE FLOW CARD (Always prominent) */}
                    {(() => {
                        const active = myVisits.find(v => ['scheduled', 'on_the_way', 'ongoing'].includes(v.status));
                        if (active) return <div className="mb-6"><ActiveWorkflowCard visit={active} onDepart={handleDepart} onArrive={handleWorkflowArrive} onInspect={handleWorkflowInspection} onWork={handleWorkflowWork} onFinal={handleWorkflowFinal} /></div>;
                        return null;
                    })()}

                    {/* --- DASHBOARD VIEW (APP HOME) --- */}
                    {view === 'dashboard' && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {/* Stats Overview */}
                            <div className="grid grid-cols-3 gap-2.5 mb-6">
                                <div className="bg-rose-500 rounded-lg shadow-sm p-2 text-white border-b-2 border-rose-700/30">
                                    <span className="block text-xl font-black">{urgentReports.length}</span>
                                    <span className="text-[8px] font-black uppercase tracking-widest opacity-90">Critical</span>
                                </div>
                                <div className="bg-teal-500 rounded-lg shadow-sm p-2 text-white border-b-2 border-teal-700/30">
                                    <span className="block text-xl font-black">{recentReports.length}</span>
                                    <span className="text-[8px] font-black uppercase tracking-widest opacity-90">Active Tasks</span>
                                </div>
                                <div className="bg-indigo-500 rounded-lg shadow-sm p-2 text-white border-b-2 border-indigo-700/30">
                                    <span className="block text-xl font-black">{centers.length}</span>
                                    <span className="text-[8px] font-black uppercase tracking-widest opacity-90">Network</span>
                                </div>
                            </div>

                            {/* Quick Actions Grid (Mobile Menu Interface) */}
                            {/* Quick Actions Grid (Mobile Menu Interface) */}
                            {/* Quick Actions Grid (Mobile Menu Interface - 4 Main Buttons) */}
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-5 px-1">Services & Operations</h3>
                            <div className="grid grid-cols-4 gap-x-2 mb-8 px-1">
                                <DashboardMenuTile
                                    icon={<MdLocationOn size={26} />}
                                    label="Visit Center"
                                    color="#D53F8C"
                                    onClick={() => setView('audit')}
                                />
                                <DashboardMenuTile
                                    icon={<MdAssignment size={26} />}
                                    label="Working Reports"
                                    color="#3182CE"
                                    onClick={() => setView('reports')}
                                />
                                <DashboardMenuTile
                                    icon={<MdAccessTime size={26} />}
                                    label="My Inventory"
                                    color="#DD6B20"
                                    onClick={() => setView('schedule')}
                                />
                                <DashboardMenuTile
                                    icon={<MdPerson size={26} />}
                                    label="Profile"
                                    color="#805AD5"
                                    onClick={() => setShowProfileModal(true)}
                                />
                            </div>

                            {/* Recent Activity Mini-Feed */}
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-widest px-1">Recent Updates</h3>
                                <button
                                    onClick={() => setView('reports')}
                                    className="text-[10px] font-black text-[#008891] uppercase tracking-tighter"
                                >
                                    View All
                                </button>
                            </div>
                            <div className="space-y-3 pb-8">
                                {urgentReports.slice(0, 2).map(center => (
                                    <div key={center.id} onClick={() => setSelectedCenter(center)} className="bg-white p-4 rounded-xl border border-slate-100 flex gap-4 items-center shadow-sm">
                                        <div className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                                            <MdWarning size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-slate-800">{center.center_name}</h4>
                                            <p className="text-[10px] text-red-400 font-bold uppercase">Critical Attention Required</p>
                                        </div>
                                        <MdArrowForward className="text-slate-300" />
                                    </div>
                                ))}
                                {resolvedReports.slice(0, 1).map(center => (
                                    <div key={center.id} onClick={() => setSelectedCenter(center)} className="bg-white p-4 rounded-xl border border-slate-100 flex gap-4 items-center shadow-sm">
                                        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center shrink-0">
                                            <MdCheckCircle size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-slate-800">{center.center_name}</h4>
                                            <p className="text-[10px] text-emerald-500 font-bold uppercase">Recently Fixed</p>
                                        </div>
                                        <MdArrowForward className="text-slate-300" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* --- WORKING REPORTS FORM (PRECISE IMAGE MATCH) --- */}
                    {view === 'reports' && (
                        <div className="bg-slate-100 px-[2px] py-4 sm:p-6 pb-24 min-h-screen">
                            <div className="max-w-4xl mx-auto bg-white border-[1.5px] border-slate-900 p-[2px] sm:p-4 shadow-2xl overflow-hidden">
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <h1 className="text-lg sm:text-xl font-black text-red-600 uppercase tracking-[0.1em] sm:tracking-[0.2em] border-b-2 border-red-100 pb-1 mb-1 inline-block">SITE ENGINEER REPORT</h1>
                                </div>

                                {/* Table Structure */}
                                <div className="border-[1.5px] border-slate-900 overflow-hidden">
                                    {/* Top Metadata */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2">
                                        <div className="flex border-b-[1.5px] border-r-[0px] sm:border-r-[1.5px] border-slate-900 min-h-[38px] sm:min-h-[44px]">
                                            <div className="w-[85px] sm:w-36 p-1.5 sm:p-2 text-[9px] sm:text-[10px] font-bold border-r-[1.5px] border-slate-900 flex items-center">Center Name:</div>
                                            <div className="flex-1 p-[2px] sm:p-1 overflow-hidden">
                                                <select
                                                    className="w-full h-full bg-slate-50 outline-none text-[9px] sm:text-[11px] font-black px-1 sm:px-2 border border-slate-200 rounded"
                                                    value={manualReportCenterId}
                                                    onChange={(e) => setManualReportCenterId(e.target.value)}
                                                >
                                                    <option value="">-- Select Center --</option>
                                                    {centers.map(c => <option key={c.id} value={c.id}>{c.center_name}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex border-b-[1.5px] border-slate-900 min-h-[38px] sm:min-h-[44px]">
                                            <div className="w-[85px] sm:w-36 p-1.5 sm:p-2 text-[9px] sm:text-[10px] font-bold border-r-[1.5px] border-slate-900 flex items-center">Visit Date:</div>
                                            <div className="flex-1 p-1.5 sm:p-2 text-[10px] sm:text-[11px] font-medium flex items-center">{format(new Date(), 'dd-MM-yyyy')}</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2">
                                        <div className="flex border-b-[1.5px] border-r-[0px] sm:border-r-[1.5px] border-slate-900 min-h-[38px] sm:min-h-[44px]">
                                            <div className="w-[85px] sm:w-36 p-1.5 sm:p-2 text-[9px] sm:text-[10px] font-bold border-r-[1.5px] border-slate-900 flex items-center">Onsite Contact:</div>
                                            <div className="flex-1 p-0.5 sm:p-1 overflow-hidden">
                                                <input type="text" className="w-full h-full outline-none text-[10px] sm:text-[11px] font-medium px-1 sm:px-2" placeholder="Contact Person" value={workData.customer_name} onChange={(e) => setWorkData({ ...workData, customer_name: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="flex border-b-[1.5px] border-slate-900 min-h-[38px] sm:min-h-[44px]">
                                            <div className="w-[85px] sm:w-36 p-1.5 sm:p-2 text-[9px] sm:text-[10px] font-bold border-r-[1.5px] border-slate-900 flex items-center">Arrival time:</div>
                                            <div className="flex-1 p-0.5 sm:p-1 overflow-hidden">
                                                <input type="time" className="w-full h-full outline-none text-[10px] sm:text-[11px] font-medium px-1 sm:px-2" value={workData.arrival_time} onChange={(e) => setWorkData({ ...workData, arrival_time: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2">
                                        <div className="flex border-b-[1.5px] border-r-[0px] sm:border-r-[1.5px] border-slate-900 min-h-[38px] sm:min-h-[44px]">
                                            <div className="w-[85px] sm:w-36 p-1.5 sm:p-2 text-[9px] sm:text-[10px] font-bold border-r-[1.5px] border-slate-900 flex items-center">Offsite time:</div>
                                            <div className="flex-1 p-0.5 sm:p-1 overflow-hidden">
                                                <input type="time" className="w-full h-full outline-none text-[10px] sm:text-[11px] font-medium px-1 sm:px-2" value={workData.offsite_time} onChange={(e) => setWorkData({ ...workData, offsite_time: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="flex border-b-[1.5px] border-slate-900 min-h-[44px] bg-slate-50/30">
                                            <div className="flex-1"></div>
                                        </div>
                                    </div>

                                    {/* Work Description AREA */}
                                    <div className="border-b-[1.5px] border-slate-900 p-2 bg-white relative">
                                        <label className="block text-[10px] font-bold mb-1 underline">Detailed description of work completed:</label>
                                        <textarea
                                            className="w-full min-h-[50px] outline-none text-xs leading-5 bg-[repeating-linear-gradient(transparent,transparent_19px,#cbd5e1_19px,#cbd5e1_20px)] px-1 font-medium"
                                            value={workData.work_description || ''}
                                            placeholder="Max 2 lines recommended..."
                                            onChange={(e) => setWorkData({ ...workData, work_description: e.target.value })}
                                        />
                                    </div>

                                    {/* Checklist Section */}
                                    <div className="grid grid-cols-2 text-[7.5px] sm:text-[9.5px] font-bold">
                                        <div className="flex border-r-[1.5px] border-slate-900 overflow-hidden">
                                            <div className="p-1 sm:p-2 border-r-[1.5px] border-slate-900 flex-1 flex items-center leading-[1.1]">* Parts used?</div>
                                            <div className="w-[45px] sm:w-[90px] p-[2px] sm:p-2 flex flex-col sm:flex-row gap-[1px] sm:gap-3 justify-center items-center">
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">Yes <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.parts_used === 'Yes'} onChange={() => setWorkData({ ...workData, parts_used: 'Yes' })} /></label>
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">No <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.parts_used === 'No'} onChange={() => setWorkData({ ...workData, parts_used: 'No' })} /></label>
                                            </div>
                                        </div>
                                        <div className="flex overflow-hidden">
                                            <div className="p-1 sm:p-2 border-r-[1.5px] border-slate-900 flex-1 flex items-center leading-[1.1]">* Issue Resolved?</div>
                                            <div className="w-[45px] sm:w-[90px] p-[2px] sm:p-2 flex flex-col sm:flex-row gap-[1px] sm:gap-3 justify-center items-center">
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">Yes <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.issue_resolved === 'Yes'} onChange={() => setWorkData({ ...workData, issue_resolved: 'Yes' })} /></label>
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">No <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.issue_resolved === 'No'} onChange={() => setWorkData({ ...workData, issue_resolved: 'No' })} /></label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 text-[7.5px] sm:text-[9.5px] font-bold border-t-[1.5px] border-slate-900">
                                        <div className="flex border-r-[1.5px] border-slate-900 overflow-hidden">
                                            <div className="p-1 sm:p-2 border-r-[1.5px] border-slate-900 flex-1 flex items-center leading-[1.1]">* Parts left onsite?</div>
                                            <div className="w-[45px] sm:w-[90px] p-[2px] sm:p-2 flex flex-col sm:flex-row gap-[1px] sm:gap-3 justify-center items-center">
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">Yes <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.parts_left_onsite === 'Yes'} onChange={() => setWorkData({ ...workData, parts_left_onsite: 'Yes' })} /></label>
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">No <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.parts_left_onsite === 'No'} onChange={() => setWorkData({ ...workData, parts_left_onsite: 'No' })} /></label>
                                            </div>
                                        </div>
                                        <div className="flex overflow-hidden">
                                            <div className="p-1 sm:p-2 border-r-[1.5px] border-slate-900 flex-1 flex items-center leading-[1.1]">* Is another visit required?</div>
                                            <div className="w-[45px] sm:w-[90px] p-[2px] sm:p-2 flex flex-col sm:flex-row gap-[1px] sm:gap-3 justify-center items-center">
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">Yes <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.visit_required === 'Yes'} onChange={() => setWorkData({ ...workData, visit_required: 'Yes' })} /></label>
                                                <label className="flex items-center gap-1 cursor-pointer w-full justify-center">No <input type="checkbox" className="w-[9px] h-[9px] sm:w-3 sm:h-3 accent-slate-900" checked={workData.visit_required === 'No'} onChange={() => setWorkData({ ...workData, visit_required: 'No' })} /></label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Parts Grid - Dynamic Multiline Support */}
                                    <div className="border-t-[1.5px] border-slate-900 text-[9.5px] font-black uppercase">
                                        <div className="p-1 px-2 bg-slate-100 flex justify-between items-center border-b-[1.5px] border-slate-900 overflow-hidden flex-nowrap">
                                            <span className="text-[8px] sm:text-[9.5px] font-black whitespace-nowrap truncate mr-2">HARDWARE MAINTENANCE TRACKING</span>
                                            <button onClick={addHardwareItem} className="bg-indigo-600 text-white px-2 py-0.5 rounded text-[7.5px] hover:bg-indigo-700 whitespace-nowrap">+ Add More</button>
                                        </div>

                                        {hardwareItems.map((item, index) => (
                                            <div key={item.id} className={`grid grid-cols-1 sm:grid-cols-2 ${index !== hardwareItems.length - 1 ? 'border-b-[1.5px] border-slate-900' : ''}`}>
                                                <div className="border-r-[0px] sm:border-r-[1.5px] border-slate-900 relative">
                                                    {hardwareItems.length > 1 && (
                                                        <button
                                                            onClick={() => removeHardwareItem(item.id)}
                                                            className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-[10px] hover:bg-red-500 hover:text-white transition-all z-10"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                    <div className="flex border-b-[1.5px] border-slate-900 h-9">
                                                        <div className="w-32 sm:w-36 p-1.5 border-r-[1.5px] border-slate-900 flex items-center bg-slate-50">#{index + 1} Removed P/N</div>
                                                        <input type="text" className="flex-1 p-1.5 outline-none font-bold text-red-600" value={item.removed_no} onChange={(e) => updateHardwareItem(item.id, 'removed_no', e.target.value)} placeholder="Part Number" />
                                                    </div>
                                                    <div className="flex h-9">
                                                        <div className="w-32 sm:w-36 p-1.5 border-r-[1.5px] border-slate-900 flex items-center bg-slate-50">#{index + 1} Removed S/N</div>
                                                        <input type="text" className="flex-1 p-1.5 outline-none text-red-500" value={item.removed_sn} onChange={(e) => updateHardwareItem(item.id, 'removed_sn', e.target.value)} placeholder="Serial Number" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex border-b-[1.5px] border-slate-900 h-9">
                                                        <div className="w-32 sm:w-36 p-1.5 border-r-[1.5px] border-slate-900 flex items-center bg-teal-50">#{index + 1} Installed P/N</div>
                                                        <input type="text" className="flex-1 p-1.5 outline-none font-bold text-teal-600" value={item.installed_no} onChange={(e) => updateHardwareItem(item.id, 'installed_no', e.target.value)} placeholder="Part Number" />
                                                    </div>
                                                    <div className="flex h-9">
                                                        <div className="w-32 sm:w-36 p-1.5 border-r-[1.5px] border-slate-900 flex items-center bg-teal-50">#{index + 1} Installed S/N</div>
                                                        <input type="text" className="flex-1 p-1.5 outline-none text-teal-500" value={item.installed_sn} onChange={(e) => updateHardwareItem(item.id, 'installed_sn', e.target.value)} placeholder="Serial Number" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Engineer Footer */}
                                    <div className="grid grid-cols-2 border-t-[1.5px] border-slate-900 min-h-[80px]">
                                        <div className="p-4 flex flex-col justify-end border-r-[1.5px] border-slate-900">
                                            <div className="flex items-end gap-2 text-[11px] font-bold border-b-[1.5px] border-slate-400 pb-1">
                                                <span>Engineer Name:</span>
                                                <span className="flex-1 px-2 italic text-indigo-600">{user?.name}</span>
                                            </div>
                                        </div>
                                        <div className="p-2 flex flex-col items-center gap-1 bg-white">
                                            <div className="w-full border border-dashed border-slate-300 rounded overflow-hidden">
                                                <SignaturePad onEnd={setEngineerSign} height={60} />
                                            </div>
                                            <div className="text-[10px] font-black underline uppercase">Engineer Sign</div>
                                        </div>
                                    </div>

                                    {/* POC SECTION */}
                                    <div className="bg-slate-100 p-2 text-[9px] font-medium leading-[1.4] italic border-t-[1.5px] border-slate-900 border-b-[1.5px]">
                                        This section must be completed by the on-site point of contact. To the POC: please check the boxes, sign, print your name, and include the date of service below.
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2">
                                        <div className="flex flex-col border-r-[0px] sm:border-r-[1.5px] border-slate-900">
                                            <div className="flex flex-1 border-b-[1.5px] border-slate-900 min-h-[60px]">
                                                <div className="flex-1 p-2 text-[9px] font-bold leading-tight flex items-center">I certify that the arrival and release times on this report are correct.</div>
                                                <div className="p-2 flex gap-3 items-center border-l border-slate-300 bg-slate-50/50">
                                                    <label className="flex items-center gap-1 text-[9px] font-bold">Yes <input type="checkbox" className="w-3 h-3" checked={workData.poc_arrival_correct === 'Yes'} onChange={() => setWorkData({ ...workData, poc_arrival_correct: 'Yes' })} /></label>
                                                    <label className="flex items-center gap-1 text-[9px] font-bold">No <input type="checkbox" className="w-3 h-3" checked={workData.poc_arrival_correct === 'No'} onChange={() => setWorkData({ ...workData, poc_arrival_correct: 'No' })} /></label>
                                                </div>
                                            </div>
                                            <div className="flex flex-1 border-b-[1.5px] sm:border-b-0 border-slate-900 min-h-[60px]">
                                                <div className="flex-1 p-2 text-[9px] font-bold leading-tight flex items-center">I also certify that I am satisfied with the work completed.</div>
                                                <div className="p-2 flex gap-3 items-center border-l border-slate-300 bg-slate-50/50">
                                                    <label className="flex items-center gap-1 text-[9px] font-bold">Yes <input type="checkbox" className="w-3 h-3" checked={workData.poc_satisfaction === 'Yes'} onChange={() => setWorkData({ ...workData, poc_satisfaction: 'Yes' })} /></label>
                                                    <label className="flex items-center gap-1 text-[9px] font-bold">No <input type="checkbox" className="w-3 h-3" checked={workData.poc_satisfaction === 'No'} onChange={() => setWorkData({ ...workData, poc_satisfaction: 'No' })} /></label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2">
                                            <div className="flex flex-col p-2 items-center bg-white border-r-[1.5px] border-slate-900">
                                                <div className="w-full border border-dashed border-indigo-50 rounded overflow-hidden">
                                                    <SignaturePad onEnd={setSignatureData} height={60} />
                                                </div>
                                                <div className="text-[9px] font-bold uppercase underline mt-1">Center Head Sign</div>
                                            </div>
                                            <div className="flex flex-col p-2 items-center justify-center bg-white">
                                                <input
                                                    type="text"
                                                    className="w-full text-center outline-none border-b-[1.5px] border-slate-400 text-[10px] font-bold italic"
                                                    placeholder="Name"
                                                    value={workData.customer_name || ''}
                                                    onChange={(e) => setWorkData({ ...workData, customer_name: e.target.value })}
                                                />
                                                <div className="text-[9px] font-bold uppercase underline mt-1">Center Head Name</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* POC COMMENTS */}
                                    <div className="border-t-[1.5px] border-slate-900 p-2 min-h-[50px] bg-white relative">
                                        <label className="block text-[10px] font-bold mb-1 underline">Details of parts/saman returning to Head Office:</label>
                                        <textarea
                                            className="w-full min-h-[50px] outline-none text-xs leading-5 bg-[repeating-linear-gradient(transparent,transparent_19px,#cbd5e1_19px,#cbd5e1_20px)] px-1 font-medium"
                                            placeholder="Max 2 lines..."
                                            value={workData.poc_comments}
                                            onChange={(e) => setWorkData({ ...workData, poc_comments: e.target.value })}
                                        />
                                    </div>

                                    {/* Footer Footer */}
                                    <div className="p-2 text-[8px] font-bold border-t-[1.5px] border-slate-900 italic leading-tight uppercase bg-slate-50 opacity-0 pointer-events-none">
                                        REMOVED NOTES SECTION
                                    </div>
                                </div>

                                {/* Digital Attachment Section */}
                                <div className="mt-8 border-2 border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/50">
                                    <h3 className="text-sm font-black text-slate-800 mb-4 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                                        DIGITAL FILE ATTACHMENTS (MANDATORY)
                                    </h3>
                                    <CameraCapture label="Capture On-Site Work Evidence (Repair Photo)" onCapture={(file) => setCheckoutData({ ...checkoutData, evidence_photo: file })} />
                                </div>

                                {/* Control Buttons */}
                                <div className="mt-8 flex flex-col sm:flex-row gap-4 px-2 sm:px-0">
                                    <button
                                        onClick={() => setView('dashboard')}
                                        className="w-full sm:w-auto px-10 py-3 bg-white border-2 border-slate-200 text-slate-500 font-bold rounded text-[10px] uppercase tracking-widest hover:border-slate-800 transition-all active:scale-95 order-2 sm:order-1"
                                    >
                                        Discard
                                    </button>
                                    <button
                                        onClick={confirmSubmitManualWork}
                                        disabled={submitting}
                                        className="flex-1 py-3 bg-slate-900 text-white font-bold rounded shadow-lg hover:shadow-none transition-all active:scale-[0.98] disabled:opacity-20 uppercase tracking-[0.2em] text-xs order-1 sm:order-2"
                                    >
                                        {submitting ? 'GENERATING REPORT...' : 'FINALIZE & SUBMIT REPORT'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}



                    {/* --- CENTER AUDIT SECTION (AD-HOC) --- */}
                    {view === 'audit' && (
                        <div className="space-y-6 pb-20 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-[#008891] to-[#0f3057] p-6 text-white text-center relative gap-4">
                                    <button
                                        onClick={() => setView('dashboard')}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 rounded-xl text-white backdrop-blur-md active:scale-90 transition-all"
                                    >
                                        <MdArrowBack size={20} />
                                    </button>
                                    <h2 className="text-xl font-bold px-8">Physical Visit Reports and Final Conclusion</h2>
                                    <p className="text-xs opacity-90 mt-1">Submit digital inspection report and problem identification.</p>
                                </div>

                                <div className="p-6 space-y-8">
                                    {/* 1. Center Selection */}
                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                                            <MdLocationOn className="text-[#008891]" /> 1. Select Center for Inspection
                                        </label>
                                        <select
                                            value={auditCenterId}
                                            onChange={(e) => setAuditCenterId(e.target.value)}
                                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-4 text-sm font-semibold outline-none focus:border-[#008891] focus:ring-4 focus:ring-[#008891]/5 transition-all appearance-none"
                                        >
                                            <option value="">-- Choose Center --</option>
                                            {centers.map(center => (
                                                <option key={center.id} value={center.id}>{center.center_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Itemized Inspection moved up as Step 2 of the form (after center select) */}
                                    <div className="space-y-4 pt-4 border-t border-slate-100">
                                        <label className="block text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                                            <MdFilterList className="text-[#008891]" /> 2. Hardware Audit & Inspection Checklist
                                        </label>

                                        <div className="space-y-3">
                                            {auditItems.map((item, idx) => (
                                                <div key={item.id} className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-200 relative group animate-in slide-in-from-left duration-300">
                                                    <div className="grid grid-cols-12 gap-2 sm:gap-3 items-end">
                                                        <div className="col-span-2 sm:col-span-1 flex flex-col justify-end">
                                                            <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1 text-center">S.N</label>
                                                            <div className="bg-slate-100 border border-slate-200 rounded-lg p-2.5 text-xs font-black text-slate-500 text-center flex items-center justify-center">
                                                                {idx + 1}
                                                            </div>
                                                        </div>
                                                        <div className="col-span-10 sm:col-span-5 md:col-span-3">
                                                            <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1">Hardware Items *</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Hardware Item Name"
                                                                className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs font-bold shadow-sm outline-none"
                                                                value={item.category}
                                                                onChange={(e) => setAuditItems(auditItems.map(ai => ai.id === item.id ? { ...ai, category: e.target.value } : ai))}
                                                            />
                                                        </div>
                                                        <div className="col-span-12 sm:col-span-6 md:col-span-5">
                                                            <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1 text-red-500">Problem Found *</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Describe problem"
                                                                className="w-full bg-white border border-red-100 rounded-lg p-2.5 text-xs font-bold shadow-sm outline-none"
                                                                value={item.problem}
                                                                onChange={(e) => setAuditItems(auditItems.map(ai => ai.id === item.id ? { ...ai, problem: e.target.value } : ai))}
                                                            />
                                                        </div>
                                                        <div className="col-span-12 sm:col-span-6 md:col-span-3">
                                                            <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1">Neets to Parts</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Parts"
                                                                className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs shadow-sm outline-none"
                                                                value={item.req_assets}
                                                                onChange={(e) => setAuditItems(auditItems.map(ai => ai.id === item.id ? { ...ai, req_assets: e.target.value } : ai))}
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setAuditItems(auditItems.filter(ai => ai.id !== item.id))}
                                                        className="absolute -top-3 -right-3 bg-white text-red-500 rounded-full w-7 h-7 flex items-center justify-center border border-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                                                    >
                                                        <MdClose size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => setAuditItems([...auditItems, { id: Date.now(), sn: auditItems.length + 1, category: '', problem: '', req_assets: '' }])}
                                                className="w-full py-3 border-2 border-dashed border-teal-200 rounded-xl text-[#008891] font-bold text-xs hover:bg-teal-50/50 transition-all uppercase"
                                            >
                                                + Add More Items
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        onClick={confirmSubmitAdHocAudit}
                                        disabled={submitting}
                                        className="w-full py-4 bg-gradient-to-r from-[#008891] to-[#0f3057] text-white font-bold rounded-xl shadow-xl hover:shadow-[#008891]/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3 text-base"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <MdCheckCircle size={24} /> Continue & Take Live Photo
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- SCHEDULE VIEW --- */}
                    {view === 'schedule' && (
                        <div className="space-y-4 pb-20 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex items-center gap-3 mb-4">
                                <button
                                    onClick={() => setView('dashboard')}
                                    className="p-2 bg-white rounded-xl border border-slate-100 text-slate-500 shadow-sm active:scale-90 transition-all"
                                >
                                    <MdArrowBack size={20} />
                                </button>
                                <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight flex-1">Your Schedules</h2>
                            </div>

                            {myVisits.filter(v => v.status === 'scheduled').length > 0 ? (
                                <div className="space-y-3">
                                    {myVisits.filter(v => v.status === 'scheduled').map(visit => (
                                        <div key={visit.id} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                                                <MdAccessTime size={24} />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-bold text-slate-800">{visit.center?.center_name || 'Assigned Center'}</h4>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                                                    Scheduled: {visit.visit_date ? format(new Date(visit.visit_date), 'dd MMM yyyy') : 'No date set'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setActiveWorkflowVisit(visit);
                                                    setView('reports');
                                                }}
                                                className="bg-slate-50 text-[#008891] p-2 rounded-lg"
                                            >
                                                <MdArrowForward size={20} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-16 bg-white rounded-3xl border-2 border-dashed border-slate-100 px-6">
                                    <div className="w-20 h-20 bg-slate-50 text-slate-200 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-inner">
                                        <MdSchedule size={40} />
                                    </div>
                                    <h3 className="text-base font-bold text-slate-800 mb-1">No Schedule Found</h3>
                                    <p className="text-xs text-slate-400 font-medium max-w-[200px] mx-auto leading-relaxed">You don't have any centers scheduled for visit right now.</p>
                                    <button
                                        onClick={() => setView('dashboard')}
                                        className="mt-6 px-6 py-2.5 bg-[#008891] text-white rounded-full text-xs font-bold shadow-lg shadow-[#008891]/20"
                                    >
                                        Back to Home
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* --- MOBILE NAVIGATION (BOTTOM BAR) --- */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 z-[100] md:hidden px-4 h-16 flex items-center justify-around shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
                <button
                    onClick={() => setView('dashboard')}
                    className={`flex flex-col items-center gap-1 transition-all ${view === 'dashboard' ? 'text-[#008891] scale-110' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <MdDashboard size={20} />
                    <span className="text-[9px] font-black uppercase tracking-tighter">Home</span>
                </button>
                <button
                    onClick={() => setView('reports')}
                    className={`flex flex-col items-center gap-1 transition-all ${view === 'reports' ? 'text-[#008891] scale-110' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <MdAssignment size={20} />
                    <span className="text-[9px] font-black uppercase tracking-tighter">Working Reports</span>
                </button>
                <div className="relative -mt-10">
                    <button
                        onClick={() => setView('audit')}
                        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all border-4 border-white ${view === 'audit' ? 'bg-[#008891] text-white scale-110' : 'bg-[#0f3057] text-white hover:bg-slate-800'}`}
                    >
                        <MdPhotoCamera size={26} />
                    </button>
                    <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[8px] font-black text-slate-500 uppercase tracking-tighter whitespace-nowrap">Visit Center</span>
                </div>
                <button
                    onClick={() => setView('schedule')}
                    className={`flex flex-col items-center gap-1 transition-all ${view === 'schedule' ? 'text-[#008891] scale-110' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <MdSchedule size={20} />
                    <span className="text-[9px] font-black uppercase tracking-tighter">My Inventory</span>
                </button>
                <button
                    onClick={() => { setShowProfileModal(true); }}
                    className={`flex flex-col items-center gap-1 transition-all text-slate-400`}
                >
                    <MdPerson size={20} />
                    <span className="text-[9px] font-black uppercase tracking-tighter">Account</span>
                </button>
            </div>

            {/* PROFILE POPUP MODAL */}
            {
                showProfileModal && (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowProfileModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm relative z-10 shadow-2xl overflow-hidden">
                            <button onClick={() => setShowProfileModal(false)} className="absolute top-3 right-3 z-20 text-slate-400 hover:text-slate-700 bg-white/80 hover:bg-white w-7 h-7 rounded-full flex items-center justify-center transition-colors shadow-sm">
                                <MdClose size={18} />
                            </button>

                            <div className="h-24 bg-gradient-to-r from-[#008891] to-[#0f3057] relative"></div>

                            <div className="-mt-10 text-center relative z-10 px-6 pb-6">
                                <div className="w-20 h-20 rounded-full border-3 border-white bg-white mx-auto flex items-center justify-center text-3xl font-bold text-[#008891] shadow-lg mb-3 overflow-hidden">
                                    {user?.profile_image ? (
                                        <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        user?.name?.charAt(0)
                                    )}
                                </div>

                                <h2 className="text-xl font-bold text-slate-800 mb-0.5">{user?.name}</h2>
                                <p className="text-[#008891] font-semibold text-[10px] uppercase tracking-wider mb-5">Field Service Engineer</p>

                                <div className="space-y-2 text-left">
                                    <div className="bg-slate-50/70 p-2.5 rounded-lg flex items-center gap-2.5 border border-slate-100/50">
                                        <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#008891] shadow-sm shrink-0">
                                            <MdEmail size={14} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[9px] uppercase font-semibold text-slate-500 mb-0.5">Email</p>
                                            <p className="font-semibold text-slate-700 text-[11px] truncate">{user?.email}</p>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50/70 p-2.5 rounded-lg flex items-center gap-2.5 border border-slate-100/50">
                                        <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#008891] shadow-sm shrink-0">
                                            <MdPhone size={14} />
                                        </div>
                                        <div>
                                            <p className="text-[9px] uppercase font-semibold text-slate-500 mb-0.5">Phone</p>
                                            <p className="font-semibold text-slate-700 text-[11px]">{user?.phone || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-t border-slate-100">
                                    <button onClick={handleLogout} className="w-full text-red-500 font-semibold text-xs flex items-center justify-center gap-2 hover:bg-red-50 py-2.5 rounded-lg transition-colors">
                                        <MdLogout size={16} /> Sign Out
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* DETAILED REPORT VIEW - FULL PAGE */}
            {
                selectedCenter ? (
                    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
                        {/* HEADER WITH LOGO AND BACK BUTTON */}
                        <div className="bg-white/90 backdrop-blur-md border-b border-slate-200/50 shadow-sm sticky top-0 z-30">
                            <div className="max-w-7xl mx-auto px-4 py-2.5">
                                <div className="flex justify-between items-center">
                                    {/* Brand Logo Area */}
                                    <div className="flex items-center gap-2">
                                        <img src={logo} alt="Secure Insights" className="w-8 h-5 sm:w-10 sm:h-7" />
                                        <div>
                                            <h1 className="text-sm sm:text-base font-semibold text-slate-800 leading-none tracking-tight">
                                                Secure<span className="text-[#008891]">Insights</span>
                                            </h1>
                                        </div>
                                    </div>

                                    {/* Back Button on Right */}
                                    <button
                                        onClick={() => setSelectedCenter(null)}
                                        className="flex items-center gap-1.5 text-slate-700 hover:bg-slate-100 px-3 py-1.5 rounded-lg transition-all font-medium text-sm border border-slate-200"
                                    >
                                        <MdChevronLeft size={18} /> Back to Dashboard
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* CENTER INFO SECTION */}
                        <div className="bg-slate-100 border-b border-slate-200">
                            <div className="max-w-7xl mx-auto px-4 py-3">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{selectedCenter.center_name}</h1>
                                        <p className="text-slate-600 text-xs flex items-center gap-1.5">
                                            <MdLocationOn size={14} />
                                            {selectedCenter.center_address || 'No address provided'}
                                        </p>
                                    </div>

                                    {/* Critical Issue Badge - Right Side */}
                                    <div className="flex items-center gap-2">
                                        {selectedCenter.is_urgent ? <MdWarning size={18} className="text-red-600" /> : <MdNotificationsActive size={18} className="text-[#008891]" />}
                                        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${selectedCenter.is_urgent ? 'bg-red-200 text-red-800' : 'bg-teal-200 text-teal-800'}`}>
                                            {selectedCenter.is_urgent ? 'âš ï¸ Critical Issue' : 'ðŸ“‹ Active Report'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CONTENT AREA */}
                        <div className="max-w-7xl mx-auto px-4 py-5">

                            {/* ACTIVE WORKFLOW FOR THIS CENTER (Steps 1-6) */}
                            {(() => {
                                const active = myVisits.find(v => v.center_id === selectedCenter.id && ['scheduled', 'on_the_way', 'ongoing'].includes(v.status));
                                if (active) return (
                                    <div className="mb-6">
                                        <ActiveWorkflowCard
                                            visit={active}
                                            onDepart={handleDepart}
                                            onArrive={handleWorkflowArrive}
                                            onInspect={handleWorkflowInspection}
                                            onWork={handleWorkflowWork}
                                            onFinal={handleWorkflowFinal}
                                        />
                                    </div>
                                );
                                return null;
                            })()}

                            {/* LATEST ISSUE CARD */}
                            <div className={`mb-5 rounded-xl shadow-sm p-4 ${selectedCenter.is_urgent ? 'bg-red-100 border-l-4 border-red-500' : 'bg-blue-100 border-l-4 border-sky-500'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <MdChat className={selectedCenter.is_urgent ? 'text-red-600' : 'text-sky-600'} size={20} />
                                    <span className={`text-xs font-bold uppercase ${selectedCenter.is_urgent ? 'text-red-600' : 'text-sky-600'}`}>
                                        Latest Issue Reported
                                    </span>
                                </div>
                                <p className="text-slate-700 text-base font-semibold mb-2">"{selectedCenter.remarks}"</p>
                                <p className="text-slate-500 text-xs font-medium flex items-center gap-1.5">
                                    <MdAccessTime size={14} />
                                    {selectedCenter.latest_report_created_at && formatDistanceToNow(new Date(selectedCenter.latest_report_created_at), { addSuffix: true })}
                                </p>
                            </div>

                            {/* REPORT HISTORY TABLE */}
                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
                                {/* TABLE HEADER */}
                                <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-3 py-3 md:px-5 md:py-3.5">
                                    <div className="flex justify-between items-center gap-2">
                                        <div className="min-w-0">
                                            <h2 className="text-sm md:text-lg font-bold text-white flex items-center gap-2">
                                                <MdHistory className="text-sky-400 w-5 h-5 md:w-6 md:h-6 shrink-0" />
                                                <span className="truncate">
                                                    <span className="md:hidden">Hardware History</span>
                                                    <span className="hidden md:inline">Complete Hardware Report History</span>
                                                </span>
                                            </h2>
                                            <p className="text-slate-300 text-[10px] md:text-xs mt-0.5 truncate max-w-[180px] md:max-w-none">Detailed tracking of hardware status</p>
                                        </div>
                                        <button
                                            onClick={() => setShowScheduleVisitModal(true)}
                                            className="bg-[#008891] hover:bg-[#007077] text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-semibold text-xs md:text-sm flex items-center gap-1.5 md:gap-2 transition-colors shadow-md shrink-0"
                                        >
                                            <MdSchedule className="w-4 h-4 md:w-5 md:h-5" />
                                            <span className="hidden sm:inline">Schedule Visit</span>
                                            <span className="sm:hidden">Schedule</span>
                                        </button>
                                    </div>
                                </div>

                                {/* TABLE CONTENT */}
                                <div className="overflow-x-auto">
                                    {loadingHistory ? (
                                        <div className="p-16 text-center">
                                            <div className="w-12 h-12 border-3 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                            <p className="text-slate-500 font-medium text-xs">Loading report history...</p>
                                        </div>
                                    ) : centerReports.length > 0 ? (
                                        <table className="w-full">
                                            <thead className="bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-bold text-slate-700 uppercase text-[10px] bg-slate-50">
                                                        Date
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Computers<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Mobiles<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        CCTV<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Headphones<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Mouse<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Keyboards<br /><span className="text-[9px] font-normal lowercase">Working/Total</span>
                                                    </th>
                                                    <th className="px-4 py-3 text-left font-bold text-slate-700 uppercase text-[10px] min-w-[200px]">
                                                        Remarks / Issues
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Status
                                                    </th>
                                                    <th className="px-4 py-3 text-center font-bold text-slate-700 uppercase text-[10px]">
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {centerReports.map((report, idx) => (
                                                    <tr key={report.id} className={`hover:bg-sky-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                                                        <td className="px-4 py-3.5 font-semibold text-slate-700 whitespace-nowrap text-xs">
                                                            {format(new Date(report.report_date || report.created_at), 'dd MMM yyyy, hh:mm a')}
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_computers} t={report.total_computers} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_mobiles} t={report.total_mobiles} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_cctv} t={report.total_cctv} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_headphones} t={report.total_headphones} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_mouse} t={report.total_mouse} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <Badge w={report.working_keyboards} t={report.total_keyboards} />
                                                        </td>
                                                        <td className="px-4 py-3.5 text-slate-700 font-normal text-xs">
                                                            <p className="line-clamp-2">{report.remarks || '-'}</p>
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <span className={`inline-flex px-3 py-1 rounded-lg text-[10px] font-bold uppercase shadow-sm whitespace-nowrap ${report.status === 'resolved'
                                                                ? 'bg-green-500 text-white'
                                                                : report.is_urgent
                                                                    ? 'bg-red-500 text-white'
                                                                    : 'bg-orange-500 text-white'
                                                                }`}>
                                                                {report.status === 'resolved' ? 'âœ“ Fixed' : (report.is_urgent ? '! Critical' : 'Pending')}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3.5 text-center">
                                                            <button
                                                                onClick={() => setViewReport(report)}
                                                                className="text-[#008891] hover:bg-teal-50 p-1.5 rounded-full transition-colors"
                                                            >
                                                                <MdVisibility size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="p-16 text-center">
                                            <MdInfo size={64} className="mx-auto mb-3 text-slate-300" />
                                            <p className="text-slate-500 font-medium text-base">No hardware reports found for this center.</p>
                                            <p className="text-slate-400 text-xs mt-1.5">Reports will appear here once submitted by the center.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div >
                ) : null
            }

            {/* REPORT DETAIL MODAL */}
            {
                viewReport && (
                    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setViewReport(null)}></div>
                        <div className="bg-white rounded-xl w-full max-w-md overflow-hidden relative z-10 shadow-2xl animate-fade-in-up">
                            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                                <div>
                                    <h3 className="font-bold text-slate-800 text-base">Report Details</h3>
                                    <p className="text-[10px] text-slate-500">{format(new Date(viewReport.report_date || viewReport.created_at), 'dd MMMM yyyy')}</p>
                                </div>
                                <button onClick={() => setViewReport(null)} className="p-1.5 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
                                    <MdClose size={18} />
                                </button>
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <DetailItem label="Computers" working={viewReport.working_computers} total={viewReport.total_computers} />
                                    <DetailItem label="Mobiles" working={viewReport.working_mobiles} total={viewReport.total_mobiles} />
                                    <DetailItem label="CCTV Cameras" working={viewReport.working_cctv} total={viewReport.total_cctv} />
                                    <DetailItem label="Headphones" working={viewReport.working_headphones} total={viewReport.total_headphones} />
                                    <DetailItem label="Mouse" working={viewReport.working_mouse} total={viewReport.total_mouse} />
                                    <DetailItem label="Keyboards" working={viewReport.working_keyboards} total={viewReport.total_keyboards} />
                                </div>
                                {viewReport.remarks && (
                                    <div className="mt-4 bg-amber-50 p-2.5 rounded-lg border border-amber-100 flex gap-2 items-start">
                                        <MdInfo size={14} className="text-amber-500 mt-0.5 shrink-0" />
                                        <p className="text-xs text-slate-700 italic leading-relaxed">"{viewReport.remarks}"</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* SCHEDULE VISIT MODAL */}
            {
                showScheduleVisitModal && (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowScheduleVisitModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-md p-6 relative z-10 shadow-2xl">
                            <button onClick={() => setShowScheduleVisitModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors">
                                <MdClose size={24} />
                            </button>

                            <div className="text-center mb-6">
                                <div className="w-14 h-14 bg-teal-100 text-[#008891] rounded-xl flex items-center justify-center mx-auto mb-3">
                                    <MdSchedule size={28} />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-800">Schedule Center Visit</h3>
                                <p className="text-sm text-slate-500 mt-1">Plan your visit to <span className="font-bold text-[#008891]">{selectedCenter?.center_name}</span></p>
                            </div>

                            <div className="space-y-4">
                                {/* Visit Date */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase">Visit Date & Time *</label>
                                    <input
                                        type="datetime-local"
                                        value={visitData.visit_date}
                                        onChange={(e) => setVisitData({ ...visitData, visit_date: e.target.value })}
                                        className="w-full bg-slate-50 border-2 border-slate-200 text-slate-800 rounded-lg p-3 font-semibold outline-none focus:ring-2 focus:ring-[#008891] focus:border-[#008891] transition-all text-sm"
                                        required
                                    />
                                </div>

                                {/* Remarks */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase">Visit Remarks *</label>
                                    <textarea
                                        value={visitData.remarks}
                                        onChange={(e) => setVisitData({ ...visitData, remarks: e.target.value })}
                                        placeholder="e.g., Routine inspection, Urgent repair, etc."
                                        rows="3"
                                        className="w-full bg-slate-50 border-2 border-slate-200 text-slate-800 rounded-lg p-3 font-medium outline-none focus:ring-2 focus:ring-[#008891] focus:border-[#008891] transition-all text-sm resize-none"
                                        maxLength="100"
                                        required
                                    />
                                    <p className="text-xs text-slate-400 mt-1">{visitData.remarks.length}/100 characters</p>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowScheduleVisitModal(false)}
                                    className="flex-1 py-3 text-slate-600 font-bold bg-slate-100 rounded-xl hover:bg-slate-200 text-sm transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleScheduleVisit}
                                    disabled={!visitData.visit_date || !visitData.remarks}
                                    className="flex-[2] py-3 text-white font-bold bg-[#008891] rounded-xl shadow-lg hover:bg-[#007077] text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Confirm Schedule
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* SCHEDULE MODAL */}
            {
                showScheduleModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowScheduleModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm p-5 relative z-10 shadow-2xl">
                            <div className="text-center mb-5">
                                <div className="w-10 h-10 bg-teal-50 text-[#008891] rounded-lg flex items-center justify-center mx-auto mb-2">
                                    <MdSchedule size={20} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800">Schedule Visit</h3>
                                <p className="text-[10px] text-slate-500 font-medium">Choose a date and time for this service.</p>
                            </div>

                            <div className="mb-5">
                                <input type="datetime-local" className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-lg p-3 font-medium outline-none focus:ring-2 focus:ring-[#008891]/30 focus:border-[#008891]/30 transition-all text-xs"
                                    onChange={(e) => setScheduleData({ ...scheduleData, date: e.target.value })} />
                            </div>

                            <div className="flex gap-2.5">
                                <button onClick={() => setShowScheduleModal(false)} className="flex-1 py-2.5 text-slate-500 font-semibold bg-slate-100 rounded-lg hover:bg-slate-200 text-xs transition-colors">Cancel</button>
                                <button onClick={handleSchedule} disabled={!scheduleData.date} className="flex-[2] py-2.5 text-white font-semibold bg-[#008891] rounded-lg shadow-sm hover:bg-[#007077] text-xs transition-colors disabled:opacity-50">
                                    Confirm Schedule
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ARRIVAL MODAL (Step 3) */}
            {
                showArrivalModal && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowArrivalModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative z-10 shadow-2xl max-h-[90vh] overflow-y-auto">
                            <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">Confirm Arrival</h3>
                            <div className="mb-4 space-y-4">
                                <CameraCapture label="Take Selfie with Hardware *" onCapture={setArrivalPhoto} />

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Center Head Signature *</label>
                                    <SignaturePad onEnd={setArrivalSignatureData} />
                                    <p className="text-[10px] text-slate-400 mt-1">Proof of arrival.</p>
                                </div>
                            </div>
                            <button onClick={confirmArrive} disabled={submitting} className="w-full py-3 bg-[#008891] text-white font-bold rounded-xl shadow-lg hover:bg-teal-700 transition-all disabled:opacity-50">
                                {submitting ? 'Checking In...' : 'Confirm & Check In'}
                            </button>
                            <button onClick={() => setShowArrivalModal(false)} className="w-full mt-2 py-2 text-slate-500 text-xs font-semibold hover:text-slate-800">Cancel</button>
                        </div>
                    </div>
                )
            }

            {/* INSPECTION MODAL (Step 3.5) */}
            {
                showInspectionModal && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowInspectionModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-5xl p-6 relative z-10 shadow-2xl max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">Digital Hardware Inspection</h3>
                            <p className="text-center text-slate-500 text-xs mb-6">Create a detailed list of all hardware and their current condition.</p>

                            <div className="space-y-4 mb-6">
                                {inspectionItems.map((item, idx) => (
                                    <div key={item.id} className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-200 relative animate-in fade-in slide-in-from-right-2 duration-300 group">
                                        <div className="grid grid-cols-12 gap-2 sm:gap-3 items-end">
                                            <div className="col-span-2 sm:col-span-1 flex flex-col justify-end">
                                                <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1 text-center">S.N</label>
                                                <div className="bg-slate-100 border border-slate-200 rounded-lg p-2.5 text-xs font-black text-slate-500 text-center flex items-center justify-center">{idx + 1}</div>
                                            </div>
                                            <div className="col-span-12 sm:col-span-5 md:col-span-3">
                                                <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1">Hardware Items *</label>
                                                <input
                                                    type="text"
                                                    placeholder="Hardware Item Name"
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs font-bold shadow-sm outline-none"
                                                    value={item.category}
                                                    onChange={(e) => updateInspectionItem(item.id, 'category', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-span-12 sm:col-span-6 md:col-span-5">
                                                <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1 text-red-500">Problem *</label>
                                                <input
                                                    type="text"
                                                    placeholder="Describe problem"
                                                    className="w-full bg-white border border-red-100 rounded-lg p-2.5 text-xs font-bold shadow-sm outline-none"
                                                    value={item.problem}
                                                    onChange={(e) => updateInspectionItem(item.id, 'problem', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-span-12 sm:col-span-6 md:col-span-3">
                                                <label className="block text-[8px] uppercase font-bold text-slate-400 mb-1">Assets Needs</label>
                                                <input
                                                    type="text"
                                                    placeholder="Parts"
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs shadow-sm outline-none"
                                                    value={item.req_assets}
                                                    onChange={(e) => updateInspectionItem(item.id, 'req_assets', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setInspectionItems(inspectionItems.filter(ai => ai.id !== item.id))}
                                            className="absolute -top-3 -right-3 bg-white text-red-500 rounded-full w-7 h-7 flex items-center justify-center border border-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                                        >
                                            <MdClose size={16} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={addInspectionItem}
                                    className="w-full py-3 border-2 border-dashed border-teal-200 rounded-xl text-[#008891] font-bold text-xs flex items-center justify-center gap-2 hover:bg-teal-50/50 transition-all uppercase"
                                >
                                    + Add More Items
                                </button>
                            </div>

                            <button
                                onClick={confirmSubmitInspection}
                                disabled={submitting}
                                className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-700 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl active:scale-[0.98] transition-all disabled:opacity-50"
                            >
                                {submitting ? 'Submitting Report...' : 'Submit Digital Inspection Report'}
                            </button>
                            <button onClick={() => setShowInspectionModal(false)} className="w-full mt-3 py-2 text-slate-400 text-xs font-semibold hover:text-slate-600 transition-colors">Go Back</button>
                        </div >
                    </div >
                )
            }

            {/* WORK REPORT MODAL (Step 5) */}
            {
                showWorkModal && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowWorkModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-lg relative z-10 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                            {/* Header */}
                            <div className="bg-[#0f3057] p-5 text-white flex justify-between items-center shrink-0">
                                <div>
                                    <h3 className="text-lg font-bold">SITE ENGINEER REPORT</h3>
                                    <p className="text-[10px] text-sky-200 uppercase tracking-widest font-bold">Work Completion & Inventory Audit</p>
                                </div>
                                <button onClick={() => setShowWorkModal(false)} className="text-white/60 hover:text-white transition-colors">
                                    <MdClose size={24} />
                                </button>
                            </div>

                            <div className="p-5 overflow-y-auto space-y-6">
                                {/* Basic Visit Info (Read-onlyish) */}
                                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Report Date</p>
                                        <p className="text-xs font-bold text-slate-700">{format(new Date(), 'dd MMM yyyy')}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Ticket / Visit ID</p>
                                        <p className="text-xs font-bold text-slate-700">#{activeWorkflowVisit?.id || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Detailed Description */}
                                <div>
                                    <label className="block text-[10px] font-black text-[#0f3057] mb-2 uppercase tracking-tight">Detailed description of work completed *</label>
                                    <textarea
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-4 text-xs font-medium focus:border-[#008891] focus:bg-white outline-none transition-all"
                                        rows="4"
                                        placeholder="Describe the tasks completed, systems fixed, software updated etc..."
                                        value={workData.work_description}
                                        onChange={(e) => setWorkData({ ...workData, work_description: e.target.value })}
                                        required
                                    ></textarea>
                                </div>

                                {/* Checklist Section */}
                                <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-2 border-y border-slate-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">Parts used?</span>
                                        <div className="flex bg-slate-100 rounded-lg p-1">
                                            {['Yes', 'No'].map(o => (
                                                <button
                                                    key={o}
                                                    onClick={() => setWorkData({ ...workData, parts_used: o })}
                                                    className={`px-3 py-1 rounded-md text-[9px] font-bold transition-all ${workData.parts_used === o ? 'bg-white shadow-sm text-[#008891]' : 'text-slate-400'}`}
                                                >
                                                    {o}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">Issue Resolved?</span>
                                        <div className="flex bg-slate-100 rounded-lg p-1">
                                            {['Yes', 'No'].map(o => (
                                                <button
                                                    key={o}
                                                    onClick={() => setWorkData({ ...workData, issue_resolved: o })}
                                                    className={`px-3 py-1 rounded-md text-[9px] font-bold transition-all ${workData.issue_resolved === o ? 'bg-white shadow-sm text-[#008891]' : 'text-slate-400'}`}
                                                >
                                                    {o}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">Parts left onsite?</span>
                                        <div className="flex bg-slate-100 rounded-lg p-1">
                                            {['Yes', 'No'].map(o => (
                                                <button
                                                    key={o}
                                                    onClick={() => setWorkData({ ...workData, parts_left_onsite: o })}
                                                    className={`px-3 py-1 rounded-md text-[9px] font-bold transition-all ${workData.parts_left_onsite === o ? 'bg-white shadow-sm text-[#008891]' : 'text-slate-400'}`}
                                                >
                                                    {o}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">Follow up visit?</span>
                                        <div className="flex bg-slate-100 rounded-lg p-1">
                                            {['Yes', 'No'].map(o => (
                                                <button
                                                    key={o}
                                                    onClick={() => setWorkData({ ...workData, visit_required: o })}
                                                    className={`px-3 py-1 rounded-md text-[9px] font-bold transition-all ${workData.visit_required === o ? 'bg-white shadow-sm text-[#008891]' : 'text-slate-400'}`}
                                                >
                                                    {o}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Part Tracking */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Installed Part S/N & Model</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-medium focus:ring-1 focus:ring-[#008891] outline-none"
                                            placeholder="Brand, Model, Serial No..."
                                            value={workData.installed_parts}
                                            onChange={(e) => setWorkData({ ...workData, installed_parts: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Removed Part S/N & Model</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-medium focus:ring-1 focus:ring-[#008891] outline-none"
                                            placeholder="Damaged part details..."
                                            value={workData.removed_parts}
                                            onChange={(e) => setWorkData({ ...workData, removed_parts: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Evidence Photo */}
                                <div>
                                    <label className="block text-[10px] font-black text-[#0f3057] mb-2 uppercase tracking-tight">Evidence / Repair Photo (Live) *</label>
                                    <CameraCapture label="Capture Work Evidence *" onCapture={(file) => setCheckoutData({ ...checkoutData, evidence_photo: file })} />
                                </div>

                                {/* Verification Section */}
                                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 shadow-inner">
                                    <h4 className="text-center text-[11px] font-black text-[#0f3057] mb-5 uppercase tracking-widest border-b pb-3">Center Verification</h4>

                                    <div className="mb-4">
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase">Point of Contact Name *</label>
                                        <input
                                            type="text"
                                            className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700 outline-none focus:border-[#008891] transition-all"
                                            placeholder="Enter Center Head or Lab Incharge name..."
                                            value={workData.customer_name}
                                            onChange={(e) => setWorkData({ ...workData, customer_name: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase">On-Site Customer Signature *</label>
                                        <div className="bg-white rounded-xl border-2 border-slate-100 overflow-hidden shadow-sm">
                                            <SignaturePad onEnd={setSignatureData} />
                                        </div>
                                        <p className="text-[9px] text-slate-400 mt-2 font-medium italic text-center">I certify that I am satisfied with the work completed.</p>
                                    </div>
                                </div>

                                <button
                                    onClick={confirmSubmitWork}
                                    disabled={submitting || !workData.work_description || !workData.customer_name || !signatureData}
                                    className="w-full py-4 bg-gradient-to-r from-[#008891] to-[#0f3057] text-white font-black rounded-xl shadow-xl hover:shadow-2xl active:scale-[0.98] transition-all disabled:opacity-40 uppercase tracking-widest text-sm"
                                >
                                    {submitting ? 'Processing Report...' : 'Finalize & Submit Report'}
                                </button>
                                <button onClick={() => setShowWorkModal(false)} className="w-full py-2 text-slate-400 text-xs font-bold hover:text-slate-600 transition-colors uppercase tracking-widest">Later</button>
                            </div>
                        </div>
                    </div>
                )

            }

            {/* FINAL CLOSE MODAL (Step 6) */}
            {
                showFinalModal && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowFinalModal(false)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative z-10 shadow-2xl">
                            <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">Final Checkout (Expenses)</h3>
                            <div className="space-y-4 mb-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Travel Cost</label>
                                        <input type="number" placeholder="0" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" value={checkoutData.travel_cost} onChange={(e) => setCheckoutData({ ...checkoutData, travel_cost: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Food Cost</label>
                                        <input type="number" placeholder="0" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" value={checkoutData.food_cost} onChange={(e) => setCheckoutData({ ...checkoutData, food_cost: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <button onClick={confirmFinalClose} disabled={submitting} className="w-full py-3 bg-slate-800 text-white font-bold rounded-xl shadow-lg hover:bg-slate-700 transition-all disabled:opacity-50">
                                {submitting ? 'Closing...' : 'Confirm & Close Mission'}
                            </button>
                            <button onClick={() => setShowFinalModal(false)} className="w-full mt-2 py-2 text-slate-500 text-xs font-semibold hover:text-slate-800">Cancel</button>
                        </div>
                    </div>
                )
            }

            {/* AD-HOC AUDIT: STEP 1 - LIVE PHOTO MODAL */}
            {
                auditStep === 1 && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setAuditStep(0)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative z-10 shadow-2xl">
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <MdCameraAlt size={24} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800">Step 2: Live Verification</h3>
                                <p className="text-xs text-slate-500 mt-1">Take a selfie with the Center Head at the location.</p>
                            </div>

                            <div className="mb-6">
                                <CameraCapture onCapture={setAuditSelfie} label="Verification Selfie" />
                            </div>

                            <button
                                onClick={confirmSubmitAdHocAudit}
                                className="w-full py-3 bg-teal-600 text-white font-bold rounded-xl shadow-lg hover:bg-teal-700 transition-all flex items-center justify-center gap-2"
                            >
                                Next Step: Digital Sign <MdArrowForward />
                            </button>
                            <button onClick={() => setAuditStep(0)} className="w-full mt-2 py-2 text-slate-500 text-xs font-semibold hover:text-slate-800">Back to Checklist</button>
                        </div>
                    </div>
                )
            }

            {/* AD-HOC AUDIT: STEP 2 - DIGITAL SIGNATURE MODAL */}
            {
                auditStep === 2 && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setAuditStep(1)}></div>
                        <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative z-10 shadow-2xl">
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <MdDraw size={24} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800">Final Step: Center Head Signature</h3>
                                <p className="text-xs text-slate-500 mt-1">Ask the Center Head to sign below to verify the audit.</p>
                            </div>

                            <div className="mb-6">
                                <SignaturePad onEnd={setAuditSign} />
                            </div>

                            <button
                                onClick={confirmSubmitAdHocAudit}
                                disabled={submitting}
                                className="w-full py-3 bg-slate-800 text-white font-bold rounded-xl shadow-lg hover:bg-slate-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Submitting Report...
                                    </>
                                ) : (
                                    <>
                                        <MdCheckCircle size={20} /> Final Submit Report
                                    </>
                                )}
                            </button>
                            <button onClick={() => setAuditStep(1)} className="w-full mt-2 py-2 text-slate-500 text-xs font-semibold hover:text-slate-800">Back to Photo</button>
                        </div>
                    </div>
                )
            }

        </div >
    );
};

const Badge = ({ w, t }) => (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm ${w < t
        ? 'bg-red-500 text-white'
        : 'bg-green-500 text-white'
        }`}>
        {w}/{t}
    </span>
);

const BadgeColorful = ({ w, t }) => (
    <span className={`inline-flex px-2.5 py-1 rounded-lg text-[10px] font-bold shadow-sm ${w < t
        ? 'bg-red-500 text-white'
        : 'bg-green-500 text-white'
        }`}>
        {w}/{t}
    </span>
);

// --- NEW WORKFLOW COMPONENTS ---

const ActiveWorkflowCard = ({ visit, onDepart, onArrive, onInspect, onWork, onFinal }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        if (!visit.deadline_at) return;
        const timer = setInterval(() => {
            const now = new Date();
            const end = new Date(visit.deadline_at);
            const diff = end - now;

            if (diff <= 0) {
                setTimeLeft('Time Expired');
            } else {
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                setTimeLeft(`${hours}h ${minutes}m left`);
            }
        }, 60000);

        const now = new Date();
        const end = new Date(visit.deadline_at);
        const diff = end - now;
        if (diff > 0) {
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setTimeLeft(`${hours}h ${minutes}m left`);
        } else { setTimeLeft('Expired'); }

        return () => clearInterval(timer);
    }, [visit.deadline_at]);

    // Robust Step Logic (6 Steps)
    // 1: Scheduled -> Engineer Schedules
    // 2: En Route -> Departed (48h deadline)
    // 3: Arrived -> Reach Center + Live Photo + Center Head Sig
    // 4: Working -> Work In Progress
    // 5: Work Done -> Report Submitted + Sig + Teacher sees 'Completed'
    // 6: Completed -> Final Checkout + Expenses + GPS
    const step =
        (visit.status === 'completed') ? 7 :
            (visit.visit_status === 'work_done') ? 6 :
                (visit.visit_status === 'inspected' || visit.inspection_data) ? 5 :
                    (visit.check_in_time) ? 4 :
                        (visit.departed_at) ? 2 :
                            1;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden mb-6 relative animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center animate-pulse">
                        <MdNotificationsActive size={20} className="text-amber-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-sm uppercase tracking-wide">Active Mission</h3>
                        <p className="text-xs text-slate-300">{visit.center?.center_name || 'Assigned Center'}</p>
                    </div>
                </div>
                {visit.deadline_at && (
                    <div className="text-right">
                        <p className="text-[10px] uppercase font-bold opacity-70 mb-0.5">SLA Deadline (48h)</p>
                        <div className="flex items-center gap-1.5 bg-red-500/20 px-2 py-1 rounded border border-red-500/30">
                            <MdTimer size={14} className="text-red-400" />
                            <span className="font-mono text-xs font-bold text-red-100">{timeLeft || 'calculating...'}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-5">
                {/* Visual Stepper */}
                <div className="flex items-center justify-between mb-8 relative px-2">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 -z-10"></div>
                    {[
                        { s: 1, label: 'Schedule', i: MdSchedule },
                        { s: 2, label: 'En Route', i: MdDirectionsCar },
                        { s: 4, label: 'Arrived', i: MdLocationOn },
                        { s: 5, label: 'Inspection', i: MdSearch },
                        { s: 6, label: 'Report', i: MdCheckCircle },
                        { s: 7, label: 'Done', i: MdHistory }
                    ].map((st) => (
                        <div key={st.s} className="relative group">
                            <div className={`p-2 rounded-full border-2 bg-white transition-colors duration-500 relative z-10 ${step >= st.s ? 'border-[#008891] text-[#008891] shadow-md scale-110' : 'border-slate-200 text-slate-300'}`}>
                                <st.i size={16} />
                            </div>
                            {step === st.s && <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold text-[#008891] whitespace-nowrap">{st.label}</span>}
                        </div>
                    ))}
                </div>

                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 text-center shadow-inner">
                    {step === 1 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-slate-800 mb-1">Step 1: Ready to Depart?</p>
                            <p className="text-xs text-slate-500 mb-4">Mark your status as 'On The Way' to start the SLA timer.</p>
                            <button onClick={() => onDepart(visit.id)} className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                                <MdDirectionsCar size={18} /> Step 2: Start Journey
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-slate-800 mb-1">Step 2: En Route</p>
                            <p className="text-xs text-slate-500 mb-4">You are travelling. Confirm Arrival with Live Photo & Signature.</p>
                            <button onClick={() => onArrive(visit)} className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 animate-pulse">
                                <MdLocationOn size={18} />
                                <span className="md:hidden">Step 3: Arrived</span>
                                <span className="hidden md:inline">Step 3: Reached & Verify (Live Photo)</span>
                            </button>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-slate-800 mb-1">Step 4: Arrived</p>
                            <p className="text-xs text-slate-500 mb-4">You have arrived. First, perform a digital hardware inspection.</p>
                            <button onClick={() => onInspect(visit)} className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                                <MdSearch size={18} /> Step 5: Start Inspection
                            </button>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-slate-800 mb-1">Step 5: Inspection Done</p>
                            <p className="text-xs text-slate-500 mb-4">Inspection report sent to Admin. Now proceed with repairs.</p>
                            <button onClick={() => onWork(visit)} className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                                <MdWork size={18} /> Step 6: Submit Work Report
                            </button>
                        </div>
                    )}

                    {step === 6 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-slate-800 mb-1">Step 6: Work Done (Report Submitted)</p>
                            <p className="text-xs text-slate-500 mb-4">Teacher now sees 'Completed'. Finalize expenses to close mission.</p>
                            <button onClick={() => onFinal(visit)} className="w-full py-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-lg font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                                <MdCheckCircle size={18} /> Step 7: Final Checkout (Expenses)
                            </button>
                        </div>
                    )}

                    {step === 7 && (
                        <div className="animate-in fade-in zoom-in duration-300">
                            <p className="text-sm font-bold text-green-700 mb-1">Step 7: Completed</p>
                            <p className="text-xs text-slate-500 mb-0">Mission closed. Expenses submitted. Great job!</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CameraCapture = ({ onCapture, label = "Take Photo" }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');

    const startCamera = async (mode = facingMode) => {
        try {
            setError(null);
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode } });
            setStream(mediaStream);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            }, 100);
        } catch (err) {
            setError("Camera access denied. Please allow perms.");
            console.error(err);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const switchCamera = () => {
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(newMode);
        startCamera(newMode);
    };

    const capture = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
                    setCapturedImage(URL.createObjectURL(blob));
                    onCapture(file);
                    stopCamera();
                }
            }, 'image/jpeg');
        }
    };

    const retake = () => {
        setCapturedImage(null);
        onCapture(null);
        startCamera();
    };

    useEffect(() => {
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, [stream]);

    return (
        <div className="flex flex-col items-center gap-3 w-full">
            <label className="text-xs font-bold text-slate-500 uppercase self-start">{label}</label>

            {error && <p className="text-red-500 text-xs font-bold">{error}</p>}

            {!stream && !capturedImage && (
                <button onClick={() => startCamera()} className="w-full h-48 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 transition-colors">
                    <MdCameraAlt size={32} className="mb-2" />
                    <span className="text-sm font-semibold">Tap to Open Camera</span>
                </button>
            )}

            {stream && !capturedImage && (
                <div className="relative w-full rounded-xl overflow-hidden bg-black flex justify-center border border-slate-200">
                    <video ref={videoRef} autoPlay playsInline className="w-full h-64 object-cover" />
                    <button onClick={capture} className="absolute bottom-4 left-1/2 -translate-x-1/2 w-16 h-16 bg-white/20 rounded-full border-4 border-white flex items-center justify-center shadow-lg active:scale-95 transition-transform backdrop-blur-md z-20">
                        <div className="w-12 h-12 bg-white rounded-full"></div>
                    </button>
                    {/* Switch Camera Button */}
                    <button onClick={switchCamera} className="absolute bottom-6 right-6 text-white/80 hover:text-white bg-white/10 p-2 rounded-full backdrop-blur-md z-20 hover:bg-white/20 transition-all">
                        <MdRefresh size={24} className="rotate-90" />
                    </button>
                </div>
            )}

            {capturedImage && (
                <div className="relative w-full">
                    <img src={capturedImage} alt="Captured" className="w-full h-64 object-cover rounded-xl border border-slate-200" />
                    <button onClick={retake} className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm">
                        <MdRefresh size={20} />
                    </button>
                </div>
            )}

            <canvas ref={canvasRef} className="hidden" />
        </div>
    );
};

const DetailItem = ({ label, working, total }) => {
    const isGood = working === total;
    return (
        <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-100">
            <div className="flex justify-between items-center mb-1.5">
                <span className="text-[11px] font-bold text-slate-700">{label}</span>
                {isGood ? <MdCheckCircle className="text-green-500" size={14} /> : <MdWarning className="text-red-500" size={14} />}
            </div>
            <div className="flex justify-between items-center text-xs">
                <div className="flex flex-col">
                    <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Total</span>
                    <span className="font-bold text-slate-700 text-sm">{total}</span>
                </div>
                <div className="h-6 w-px bg-slate-200"></div>
                <div className="flex flex-col items-end">
                    <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Working</span>
                    <span className={`font-bold text-sm ${isGood ? 'text-green-600' : 'text-red-600'}`}>{working}</span>
                </div>
            </div>
        </div>
    );
};

const SignaturePad = ({ onEnd, height = 160 }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getPos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        onEnd(canvas.toDataURL('image/png'));
    };

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX || e.nativeEvent.clientX;
            clientY = e.clientY || e.nativeEvent.clientY;
        }

        return {
            x: (clientX - rect.left) * (canvas.width / rect.width),
            y: (clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const clear = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onEnd(null);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            // Save content if any (optional, usually clear on resize is better for hygiene)
            const tempImage = canvas.toDataURL();
            const img = new Image();
            img.src = tempImage;

            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;

            ctx.strokeStyle = '#0f3057';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';

            img.onload = () => ctx.drawImage(img, 0, 0);
        };

        const ro = new ResizeObserver(resize);
        ro.observe(canvas);
        resize();

        return () => ro.disconnect();
    }, []);

    return (
        <div style={{ height: `${height}px` }} className="relative w-full bg-white rounded border border-slate-200 shadow-inner overflow-hidden flex flex-col">
            <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="flex-1 w-full cursor-crosshair touch-none overflow-hidden"
            />
            <button
                type="button"
                onClick={clear}
                className="absolute top-1 right-1 text-[8px] text-red-500 bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-red-100 font-bold uppercase z-10"
            >
                Clear
            </button>
        </div>
    );
};

// Helper Component for Dashboard Menu Tiles (Colorful App Style - Row Fixed)
const DashboardMenuTile = ({ icon, label, color, onClick }) => (
    <div
        onClick={onClick}
        className="flex flex-col items-center justify-start gap-1.5 active:scale-90 transition-all cursor-pointer group"
    >
        <div
            style={{ backgroundColor: `${color}15`, borderColor: `${color}30` }}
            className="w-[18vw] max-w-[64px] h-[18vw] max-h-[64px] rounded-2xl border flex items-center justify-center shadow-sm group-hover:shadow-md transition-all"
        >
            <span style={{ color: color }}>
                {icon}
            </span>
        </div>
        <span className="text-[9px] font-black text-slate-500 text-center leading-tight whitespace-nowrap uppercase tracking-tighter">{label}</span>
    </div>
);

export default EngineerDashboard;
